import React, { useMemo, useState } from "react";

export type GeoEntityMode = "province" | "ville" | "commune" | "quartier";

type GeoEntityModalProps = {
  open: boolean;
  mode: GeoEntityMode;
  parentLabel?: string;
  loading?: boolean;
  initialName?: string;
  onClose: () => void;
  onSubmit: (payload: {
    nom: string;
    actif: boolean;
    commentaire?: string;
  }) => Promise<void>;
};

const TITLES: Record<GeoEntityMode, string> = {
  province: "Province",
  ville: "Ville",
  commune: "Commune",
  quartier: "Quartier",
};

export default function GeoEntityModal({
  open,
  mode,
  parentLabel,
  loading = false,
  initialName = "",
  onClose,
  onSubmit,
}: GeoEntityModalProps): React.JSX.Element | null {
  const [nom, setNom] = useState(initialName);
  const [actif, setActif] = useState(true);
  const [commentaire, setCommentaire] = useState("");

  const title = useMemo(() => `Ajouter ${TITLES[mode]}`, [mode]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-2xl">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {parentLabel ? (
          <p className="mt-1 text-xs text-slate-400">Parent: {parentLabel}</p>
        ) : null}

        <div className="mt-4 space-y-3">
          <label className="block text-xs font-medium text-slate-400">
            Nom *
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="Nom"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={actif}
              onChange={(e) => setActif(e.target.checked)}
              className="rounded border-slate-600 bg-slate-950"
            />
            Actif
          </label>

          <label className="block text-xs font-medium text-slate-400">
            Commentaire (optionnel)
            <textarea
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:text-white"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={loading || !nom.trim()}
            onClick={() =>
              void onSubmit({
                nom: nom.trim(),
                actif,
                commentaire: commentaire.trim() || undefined,
              })
            }
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
